import type { SpotListItem } from "@divespots/contracts";
import { isClosedOn } from "@divespots/contracts";
import type { Marker, MarkerClusterGroup } from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { spotPinHtml } from "../components/Pin.tsx";
import { L } from "./leaflet-global.ts";
import "leaflet.markercluster";

interface Props {
  spots: readonly SpotListItem[];
  selectedSlug: string | undefined;
  onSelect: (slug: string) => void;
  /** Accessible names; `{{name}}` / `{{count}}` are filled in by the caller's t(). */
  pinLabel: (spot: SpotListItem, closed: boolean, favourite: boolean) => string;
  /** The signed-in diver's favourites: their pins carry a star badge (clusters get none). */
  favouriteIds?: ReadonlySet<string> | undefined;
  clusterLabel: (count: number) => string;
  now?: Date;
}

const PIN_BOX = 44; // Leaflet icon box = the 44px hit target; the 32px pin is centred inside.

function pinIcon(selected: boolean, closed: boolean, permanent: boolean, favourite: boolean) {
  return L.divIcon({
    className: "spot-marker",
    html: spotPinHtml({ selected, closed, permanent, favourite }),
    iconSize: [PIN_BOX, PIN_BOX],
    iconAnchor: [PIN_BOX / 2, PIN_BOX / 2],
  });
}

/** Spot pins in a markercluster group; cluster bubbles are the design's `ocean` circle with a count. */
export function SpotCluster({
  spots,
  selectedSlug,
  onSelect,
  pinLabel,
  clusterLabel,
  favouriteIds,
  now,
}: Props) {
  const map = useMap();
  const groupRef = useRef<MarkerClusterGroup | null>(null);
  const markers = useRef(
    new Map<
      string,
      { marker: Marker; id: string; closed: boolean; permanent: boolean; favourite: boolean }
    >(),
  );
  const favouritesRef = useRef(favouriteIds);
  favouritesRef.current = favouriteIds;
  const selectRef = useRef(onSelect);
  const labelRef = useRef({ pinLabel, clusterLabel });
  const selectedRef = useRef(selectedSlug);
  selectRef.current = onSelect;
  labelRef.current = { pinLabel, clusterLabel };
  selectedRef.current = selectedSlug;

  // (Re)build the markers when the spot list or the language (labels) changes.
  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 56,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const size = count >= 100 ? "lg" : count >= 10 ? "md" : "sm";
        const label = labelRef.current.clusterLabel(count);
        return L.divIcon({
          className: "cluster-marker",
          html: `<div class="cluster-bubble cluster-bubble--${size}"><span aria-hidden="true">${count}</span><span class="sr-only">${label}</span></div>`,
          iconSize: [44, 44],
          iconAnchor: [22, 22],
        });
      },
    });
    const byslug = markers.current;
    byslug.clear();
    const when = now ?? new Date();
    for (const spot of spots) {
      const closed = isClosedOn(spot.closedPeriods, when) !== null;
      const permanent = spot.permanentClosure !== null;
      const favourite = favouritesRef.current?.has(spot.id) === true;
      const name = labelRef.current.pinLabel(spot, closed, favourite);
      const marker = L.marker([spot.location.lat, spot.location.lon], {
        icon: pinIcon(spot.slug === selectedRef.current, closed, permanent, favourite),
        title: name,
        alt: name,
        keyboard: true,
        riseOnHover: true,
      });
      // the name can change while the marker lives (a star), so it is read from the options on every add
      marker.on("add", () =>
        marker.getElement()?.setAttribute("aria-label", marker.options.title ?? name),
      );
      marker.on("click", () => selectRef.current(spot.slug));
      byslug.set(spot.slug, { marker, id: spot.id, closed, permanent, favourite });
      group.addLayer(marker);
    }
    map.addLayer(group);
    groupRef.current = group;
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
      byslug.clear();
    };
  }, [map, spots, pinLabel, clusterLabel, now]);

  // Selected pin grows and gets its ring.
  const prev = useRef<string | undefined>(undefined);
  useEffect(() => {
    const entries = markers.current;
    const old = prev.current ? entries.get(prev.current) : undefined;
    old?.marker.setIcon(pinIcon(false, old.closed, old.permanent, old.favourite));
    const cur = selectedSlug ? entries.get(selectedSlug) : undefined;
    cur?.marker.setIcon(pinIcon(true, cur.closed, cur.permanent, cur.favourite));
    prev.current = selectedSlug;
  }, [selectedSlug, spots, pinLabel, clusterLabel, now]);

  // A star toggled elsewhere changes that one pin in place (no rebuild: clusters keep their state).
  useEffect(() => {
    for (const [slug, e] of markers.current) {
      const favourite = favouriteIds?.has(e.id) === true;
      if (favourite === e.favourite) continue;
      e.favourite = favourite;
      const spot = spots.find((s) => s.slug === slug);
      if (spot) {
        const name = labelRef.current.pinLabel(spot, e.closed, favourite);
        e.marker.options.title = name;
        e.marker.options.alt = name;
      }
      e.marker.setIcon(pinIcon(slug === selectedRef.current, e.closed, e.permanent, favourite));
      const el = e.marker.getElement();
      if (el && e.marker.options.title) el.setAttribute("aria-label", e.marker.options.title);
    }
  }, [favouriteIds, spots]);

  return null;
}
